import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { apiEndpoints } from "@/lib/api";
import type { Notification } from "../types/notifications";

// ─── Employee notifications ──────────────────────────────────────────────────

export const useNotifications = (params?: {
  limit?: number;
  cursor?: string;
  unreadOnly?: boolean;
  projectId?: string;
}) => {
  return useQuery({
    queryKey: ["notifications", params],
    queryFn: () => api.get(apiEndpoints.notifications.getAll, { params }),
    select: (res) => res.data as Notification[],
  });
};

export const useNotificationsInfinite = (params?: {
  limit?: number;
  unreadOnly?: boolean;
  projectId?: string;
}) => {
  return useInfiniteQuery({
    queryKey: ["notifications", "infinite", params],
    queryFn: ({ pageParam }) =>
      api.get(apiEndpoints.notifications.getAll, {
        params: { ...params, cursor: pageParam },
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage?.data?.pagination?.nextCursor ?? undefined,
    select: (data) => ({
      notifications: data.pages.flatMap(
        (page: any) => page.data ?? []
      ) as Notification[],
      hasMore: data.pages[data.pages.length - 1]?.data?.pagination?.hasMore ?? false,
      nextCursor:
        data.pages[data.pages.length - 1]?.data?.pagination?.nextCursor ?? null,
    }),
  });
};

export const useUnreadCount = () => {
  return useQuery({
    queryKey: ["notifications", "unread-count"],
    queryFn: () => api.get(apiEndpoints.notifications.unreadCount),
    select: (res) => res.data.unreadCount as number,
    refetchInterval: 60_000, // poll every 60s
    refetchIntervalInBackground: false, // pause when tab isn't focused
    staleTime: 30_000, // avoid duplicate fetch if another component reads this too
  });
};

export const useMarkNotificationRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.patch(apiEndpoints.notifications.markRead(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
};

export const useMarkAllNotificationsRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (projectId?: string) =>
      api.patch(apiEndpoints.notifications.markAllRead, { projectId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
};

export const useFetchMoreNotifications = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ cursor, limit = 20 }: { cursor: string; limit?: number }) =>
      api.get(apiEndpoints.notifications.getAll, { params: { cursor, limit } }),
    onSuccess: (newData, variables) => {
      queryClient.setQueryData<Notification[]>(
        ["notifications", { cursor: variables.cursor, limit: variables.limit }],
        (old = []) => [...old, ...(newData.data as Notification[])]
      );
    },
  });
};

export const useNotificationDropdown = (isOpen: boolean, isPortal = false) => {
  return useQuery({
    queryKey: isPortal
      ? ["portal", "notifications", "dropdown"]
      : ["notifications", "dropdown"],
    queryFn: () =>
      api.get(
        isPortal
          ? apiEndpoints.portal.notifications.getAll
          : apiEndpoints.notifications.getAll,
        { params: { limit: 8 } }
      ),
    enabled: isOpen,
    refetchInterval: isOpen ? 60_000 : false,
    refetchIntervalInBackground: false,
    staleTime: 0,
    select: (res) => res.data as Notification[],
  });
};
